// idea-vault/ViewIdeaModal.tsx
import React from 'react';
import { X, Clock, Users, Tag, FileText, Star, Video, FileText as FileTextIcon, MessageSquare, Mic, Sparkles, Calendar } from 'lucide-react';
import { Idea } from '../../store/ideasStore';

interface ViewIdeaModalProps {
  isOpen: boolean;
  onClose: () => void;
  idea: Idea | null;
}

const ViewIdeaModal: React.FC<ViewIdeaModalProps> = ({
  isOpen,
  onClose,
  idea
}) => {
  if (!isOpen || !idea) return null;

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return <Star className="w-5 h-5 fill-red-500 text-red-500" />;
      case 'medium': return <Star className="w-5 h-5 fill-yellow-500 text-yellow-500" />;
      case 'low': return <Star className="w-5 h-5 fill-green-500 text-green-500" />;
      default: return <Star className="w-5 h-5 text-gray-400" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'video': return <Video className="w-5 h-5 text-red-600" />;
      case 'blog': return <FileTextIcon className="w-5 h-5 text-primary-600" />;
      case 'social': return <MessageSquare className="w-5 h-5 text-green-600" />;
      case 'podcast': return <Mic className="w-5 h-5 text-purple-600" />;
      default: return <Sparkles className="w-5 h-5 text-gray-600" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-primary-100 text-primary-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-indigo-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold">
                Idea Details
              </h3>
              <p className="text-primary-100 text-sm mt-1">
                Viewing your creative idea
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Basic Information */}
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary-600" />
                  Idea Details
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Title</p>
                    <p className="text-xl font-semibold text-gray-900">{idea.title}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-500">Description</p>
                    <p className="text-gray-700 leading-relaxed">{idea.description}</p>
                  </div>
                </div>
              </div>

              {/* Status & Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">Status</p>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(idea.status)}`}>
                    {idea.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">Priority</p>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(idea.priority)}`}>
                    {getPriorityIcon(idea.priority)}
                    {idea.priority.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {/* Right Column - Categorization */}
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-green-600" />
                  Categorization
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Category</p>
                    <div className="flex items-center gap-2 mt-1">
                      {getCategoryIcon(idea.category)}
                      <span className="font-medium capitalize">{idea.category}</span>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-500">Estimated Effort</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{idea.estimatedEffort}h</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {idea.tags.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {idea.tags.map(tag => (
                      <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        <Tag className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Target Audience */}
              {idea.targetAudience.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">Target Audience</p>
                  <div className="flex flex-wrap gap-2">
                    {idea.targetAudience.map(audience => (
                      <span key={audience} className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                        <Users className="w-3 h-3" />
                        {audience}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Created Date */}
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">Created</p>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">{new Date(idea.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          {idea.notes && (
            <div className="mt-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Additional Notes
              </h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 whitespace-pre-wrap">{idea.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewIdeaModal;
